import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { muhtarApplicationsAPI, locationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  UserCheck, Search, Filter, Check, X, Loader2, Eye, Download, ChevronLeft, ChevronRight,
  Mail, Phone, MapPin, User as UserIcon, CreditCard, Calendar, Clock, Edit, Save,
} from 'lucide-react';

const STATUS_LABELS = {
  beklemede: { label: 'Bekliyor', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  onaylandi: { label: 'Onaylandi', cls: 'bg-green-100 text-green-700 border-green-200' },
  reddedildi: { label: 'Reddedildi', cls: 'bg-red-100 text-red-700 border-red-200' },
};

const APPROVER_ROLES = ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'];

export default function MuhtarApplicationsPage() {
  const { user } = useAuth();
  usePageTitle('Muhtar Basvurulari');

  const canApprove = APPROVER_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 20;

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('beklemede');
  const [ilFilter, setIlFilter] = useState('');
  const [ilceFilter, setIlceFilter] = useState('');
  const [mahalleFilter, setMahalleFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [mahalleler, setMahalleler] = useState([]);

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Detail dialog
  const [detail, setDetail] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Reject dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectIds, setRejectIds] = useState([]);
  const [actionSaving, setActionSaving] = useState(false);

  // Load il list (cascade: default to Adana's ilceler)
  useEffect(() => {
    (async () => {
      try {
        const r = await locationsAPI.getIller();
        setIller(r.data);
        const def = r.data.find(i => i.name === 'Adana') || r.data[0];
        if (def) {
          setIlFilter(def.id);
          const r2 = await locationsAPI.getIlceler(def.id);
          setIlceler(r2.data);
        }
      } catch {}
    })();
  }, []);

  const handleIlFilterChange = async (v) => {
    const val = v === 'all' ? '' : v;
    setIlFilter(val);
    setIlceFilter('');
    setMahalleFilter('');
    setMahalleler([]);
    setPage(1);
    if (val) {
      try { const r = await locationsAPI.getIlceler(val); setIlceler(r.data); }
      catch { setIlceler([]); }
    } else {
      setIlceler([]);
    }
  };

  useEffect(() => {
    if (!ilceFilter) { setMahalleler([]); setMahalleFilter(''); return; }
    locationsAPI.getMahalleler(ilceFilter).then(r => setMahalleler(r.data)).catch(() => setMahalleler([]));
  }, [ilceFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * limit, limit,
        search: search || undefined,
        status: status || undefined,
        il_id: ilFilter || undefined,
        ilce_id: ilceFilter || undefined,
        mahalle_id: mahalleFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const r = await muhtarApplicationsAPI.list(params);
      setItems(r.data.items);
      setTotal(r.data.total);
      setSelected(new Set());
    } catch (e) {
      toast.error('Basvurular yuklenemedi');
    } finally { setLoading(false); }
  }, [page, search, status, ilFilter, ilceFilter, mahalleFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [page, status, ilFilter, ilceFilter, mahalleFilter, dateFrom, dateTo]); // eslint-disable-line

  const handleSearch = (e) => { e.preventDefault(); setPage(1); load(); };
  const clearFilters = () => { setSearch(''); setStatus('beklemede'); setIlFilter(''); setIlceFilter(''); setMahalleFilter(''); setIlceler([]); setMahalleler([]); setDateFrom(''); setDateTo(''); setPage(1); setTimeout(load, 0); };

  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  const handleApprove = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} muhtar onaylanacak. Emin misiniz?`)) return;
    setActionSaving(true);
    try {
      const r = await muhtarApplicationsAPI.approve(ids);
      toast.success(r.data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Onaylama basarisiz');
    } finally { setActionSaving(false); }
  };

  const openReject = (ids) => { setRejectIds(ids); setRejectReason(''); setRejectOpen(true); };
  const submitReject = async () => {
    setActionSaving(true);
    try {
      const r = await muhtarApplicationsAPI.reject(rejectIds, rejectReason);
      toast.success(r.data.message);
      setRejectOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reddetme basarisiz');
    } finally { setActionSaving(false); }
  };

  const handleExport = async () => {
    try {
      toast.info('Excel hazirlaniyor...');
      const params = {
        status: status || undefined,
        search: search || undefined,
        il_id: ilFilter || undefined,
        ilce_id: ilceFilter || undefined,
        mahalle_id: mahalleFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const r = await muhtarApplicationsAPI.exportExcel(params);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `muhtar_basvurulari_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excel indirildi');
    } catch (e) {
      toast.error('Excel aktarimi basarisiz');
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (!canApprove) {
    return (
      <div className="p-8 text-center text-slate-500">
        <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p>Bu modulu goruntuleme yetkiniz yok.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="muhtar-applications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><UserCheck className="w-6 h-6 text-indigo-600" />Muhtar Basvurulari</h1>
          <p className="text-sm text-slate-500">Muhtar kayit onay/red yonetimi - toplam <b>{total}</b> basvuru</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn"><Download className="w-4 h-4 mr-1.5" />Excel</Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Ad, soyad, TC, telefon, e-posta..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="search-input" />
            </div>
            <Button type="submit" variant="secondary" size="sm"><Filter className="w-4 h-4 mr-1" />Ara</Button>
            {(search || status !== 'beklemede' || ilFilter || ilceFilter || mahalleFilter || dateFrom || dateTo) && (
              <Button type="button" variant="ghost" size="sm" onClick={clearFilters}><X className="w-4 h-4 mr-1" />Temizle</Button>
            )}
          </form>
          <div className="flex flex-col md:flex-row gap-2 flex-wrap">
            <Select value={status || 'all'} onValueChange={v => { setStatus(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-44 h-9" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tum Durumlar</SelectItem>
                <SelectItem value="beklemede">Bekliyor</SelectItem>
                <SelectItem value="onaylandi">Onaylandi</SelectItem>
                <SelectItem value="reddedildi">Reddedildi</SelectItem>
              </SelectContent>
            </Select>
            <Select value={ilFilter || 'all'} onValueChange={handleIlFilterChange}>
              <SelectTrigger className="w-full md:w-40 h-9" data-testid="il-filter"><SelectValue placeholder="Il" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tum Iller</SelectItem>
                {iller.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={ilceFilter || 'all'} onValueChange={v => { setIlceFilter(v === 'all' ? '' : v); setPage(1); }} disabled={!ilFilter && ilceler.length === 0}>
              <SelectTrigger className="w-full md:w-44 h-9" data-testid="ilce-filter"><SelectValue placeholder="Ilce" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tum Ilceler</SelectItem>
                {ilceler.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {mahalleler.length > 0 && (
              <Select value={mahalleFilter || 'all'} onValueChange={v => { setMahalleFilter(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-full md:w-48 h-9" data-testid="mahalle-filter"><SelectValue placeholder="Mahalle" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tum Mahalleler</SelectItem>
                  {mahalleler.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex gap-1 items-center">
              <Label className="text-xs text-slate-500">Tarih:</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36 h-9 text-xs" data-testid="date-from" />
              <span className="text-slate-400">-</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36 h-9 text-xs" data-testid="date-to" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium flex-1">{selected.size} basvuru secildi</span>
          <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={actionSaving} onClick={() => handleApprove(Array.from(selected))} data-testid="bulk-approve-btn">
            <Check className="w-4 h-4 mr-1" />Toplu Onayla
          </Button>
          <Button size="sm" variant="destructive" disabled={actionSaving} onClick={() => openReject(Array.from(selected))} data-testid="bulk-reject-btn">
            <X className="w-4 h-4 mr-1" />Toplu Reddet
          </Button>
        </div>
      )}

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-slate-500"><UserCheck className="w-10 h-10 mx-auto opacity-30 mb-2" /><p>Basvuru bulunamadi</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-3 text-left w-10"><input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleAll} data-testid="select-all-checkbox" /></th>
                    <th className="p-3 text-left">Ad Soyad</th>
                    <th className="p-3 text-left">TC No</th>
                    <th className="p-3 text-left">Telefon</th>
                    <th className="p-3 text-left">E-posta</th>
                    <th className="p-3 text-left">Ilce / Mahalle</th>
                    <th className="p-3 text-left">Tarih</th>
                    <th className="p-3 text-left">Durum</th>
                    <th className="p-3 text-right">Islem</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(u => {
                    const st = STATUS_LABELS[u.approval_status] || STATUS_LABELS.beklemede;
                    return (
                      <tr key={u.id} className="border-t hover:bg-slate-50">
                        <td className="p-3"><input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleOne(u.id)} data-testid={`select-${u.id}`} /></td>
                        <td className="p-3 font-medium">{u.name} {u.surname}</td>
                        <td className="p-3 font-mono text-xs">{u.tc_kimlik}</td>
                        <td className="p-3">{u.phone}</td>
                        <td className="p-3 text-xs">{u.email}</td>
                        <td className="p-3 text-xs">{u.ilce_name || '-'} / {u.mahalle_name || '-'}</td>
                        <td className="p-3 text-xs">{fmtDate(u.created_at)}</td>
                        <td className="p-3"><Badge className={st.cls + ' border'}>{st.label}</Badge></td>
                        <td className="p-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" className="h-7" onClick={() => setDetail(u)} data-testid={`detail-${u.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                            {u.approval_status === 'beklemede' && (
                              <>
                                <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700" disabled={actionSaving} onClick={() => handleApprove([u.id])} data-testid={`approve-${u.id}`}><Check className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="destructive" className="h-7" disabled={actionSaving} onClick={() => openReject([u.id])} data-testid={`reject-${u.id}`}><X className="w-3.5 h-3.5" /></Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-3 border-t">
              <span className="text-xs text-slate-500">Sayfa {page} / {totalPages}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setEditMode(false); setEditForm(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2"><UserIcon className="w-5 h-5" />{detail?.name} {detail?.surname}</span>
              {detail && canApprove && !editMode && (
                <Button size="sm" variant="outline" onClick={() => {
                  setEditForm({
                    name: detail.name || '', surname: detail.surname || '',
                    email: detail.email || '', phone: detail.phone || '', tc_kimlik: detail.tc_kimlik || '',
                    cinsiyet: detail.cinsiyet || '', egitim_durumu: detail.egitim_durumu || '',
                    meslek: detail.meslek || '', dogum_tarihi: detail.dogum_tarihi || '',
                    okul_adi: detail.okul_adi || '', uyesi_oldugu_kurulus: detail.uyesi_oldugu_kurulus || '',
                    acik_adres: detail.acik_adres || '',
                    muhtarlik_adresi: detail.muhtar_bilgileri?.muhtarlik_adresi || '',
                    muhtarlik_telefon: detail.muhtar_bilgileri?.muhtarlik_telefon || '',
                    aza_sayisi: detail.muhtar_bilgileri?.aza_sayisi || '',
                  });
                  setEditMode(true);
                }} data-testid="muhtar-edit-btn">
                  <Edit className="w-4 h-4 mr-1" /> Düzenle
                </Button>
              )}
            </DialogTitle>
            <DialogDescription>Muhtar basvuru detayi</DialogDescription>
          </DialogHeader>
          {detail && !editMode && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2"><Badge className={(STATUS_LABELS[detail.approval_status] || STATUS_LABELS.beklemede).cls + ' border'}>{(STATUS_LABELS[detail.approval_status] || STATUS_LABELS.beklemede).label}</Badge></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-slate-400" /><span className="font-mono text-xs">{detail.tc_kimlik}</span></div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /><span>{detail.phone}</span></div>
                <div className="flex items-center gap-2 col-span-2"><Mail className="w-4 h-4 text-slate-400" /><span>{detail.email}</span></div>
                <div className="flex items-center gap-2 col-span-2"><MapPin className="w-4 h-4 text-slate-400" /><span>{detail.il_name} / {detail.ilce_name} / {detail.mahalle_name}</span></div>
                <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /><span className="text-xs">{fmtDate(detail.created_at)}</span></div>
                {detail.updated_at && detail.updated_at !== detail.created_at && (
                  <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /><span className="text-xs">Guncelleme: {fmtDate(detail.updated_at)}</span></div>
                )}
              </div>
              {detail.muhtar_bilgileri && Object.keys(detail.muhtar_bilgileri).length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3 border space-y-1">
                  <p className="font-medium text-xs uppercase text-slate-500 mb-1">Muhtarlik Bilgileri</p>
                  {detail.muhtar_bilgileri.muhtarlik_adresi && <p><b>Adres:</b> {detail.muhtar_bilgileri.muhtarlik_adresi}</p>}
                  {detail.muhtar_bilgileri.muhtarlik_telefon && <p><b>Telefon:</b> {detail.muhtar_bilgileri.muhtarlik_telefon}</p>}
                  {detail.muhtar_bilgileri.aza_sayisi && <p><b>Aza Sayisi:</b> {detail.muhtar_bilgileri.aza_sayisi}</p>}
                  {detail.muhtar_bilgileri.rejection_reason && <p className="text-red-600"><b>Red Sebebi:</b> {detail.muhtar_bilgileri.rejection_reason}</p>}
                  {detail.muhtar_bilgileri.rejected_by_name && <p className="text-xs text-slate-500">Reddeden: {detail.muhtar_bilgileri.rejected_by_name}</p>}
                </div>
              )}
            </div>
          )}
          {detail && editMode && editForm && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Ad</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
                <div><Label className="text-xs">Soyad</Label><Input value={editForm.surname} onChange={(e) => setEditForm({ ...editForm, surname: e.target.value })} /></div>
                <div><Label className="text-xs">E-posta</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
                <div><Label className="text-xs">Telefon</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
                <div><Label className="text-xs">TC Kimlik</Label><Input value={editForm.tc_kimlik} onChange={(e) => setEditForm({ ...editForm, tc_kimlik: e.target.value.replace(/[^0-9]/g, '') })} maxLength={11} /></div>
                <div><Label className="text-xs">Doğum Tarihi</Label><Input type="date" value={editForm.dogum_tarihi} onChange={(e) => setEditForm({ ...editForm, dogum_tarihi: e.target.value })} /></div>
                <div>
                  <Label className="text-xs">Cinsiyet</Label>
                  <Select value={editForm.cinsiyet} onValueChange={(v) => setEditForm({ ...editForm, cinsiyet: v })}>
                    <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="erkek">Erkek</SelectItem>
                      <SelectItem value="kadin">Kadın</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Eğitim Durumu</Label>
                  <Select value={editForm.egitim_durumu} onValueChange={(v) => setEditForm({ ...editForm, egitim_durumu: v })}>
                    <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ilkokul">İlkokul</SelectItem>
                      <SelectItem value="ortaokul">Ortaokul</SelectItem>
                      <SelectItem value="lise">Lise</SelectItem>
                      <SelectItem value="onlisans">Önlisans</SelectItem>
                      <SelectItem value="lisans">Lisans</SelectItem>
                      <SelectItem value="yuksek_lisans">Yüksek Lisans</SelectItem>
                      <SelectItem value="doktora">Doktora</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Meslek</Label><Input value={editForm.meslek} onChange={(e) => setEditForm({ ...editForm, meslek: e.target.value })} /></div>
                <div><Label className="text-xs">Okul Adı</Label><Input value={editForm.okul_adi} onChange={(e) => setEditForm({ ...editForm, okul_adi: e.target.value })} /></div>
                <div className="col-span-2"><Label className="text-xs">Üye Olduğu Kuruluş</Label><Input value={editForm.uyesi_oldugu_kurulus} onChange={(e) => setEditForm({ ...editForm, uyesi_oldugu_kurulus: e.target.value })} /></div>
                <div className="col-span-2"><Label className="text-xs">Açık Adres</Label><Textarea value={editForm.acik_adres} onChange={(e) => setEditForm({ ...editForm, acik_adres: e.target.value })} rows={2} /></div>
              </div>
              <div className="border-t pt-3 space-y-3">
                <p className="font-medium text-xs uppercase text-slate-500">Muhtarlık Bilgileri</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label className="text-xs">Muhtarlık Adresi</Label><Input value={editForm.muhtarlik_adresi} onChange={(e) => setEditForm({ ...editForm, muhtarlik_adresi: e.target.value })} /></div>
                  <div><Label className="text-xs">Muhtarlık Telefon</Label><Input value={editForm.muhtarlik_telefon} onChange={(e) => setEditForm({ ...editForm, muhtarlik_telefon: e.target.value })} /></div>
                  <div><Label className="text-xs">Aza Sayısı</Label><Input value={editForm.aza_sayisi} onChange={(e) => setEditForm({ ...editForm, aza_sayisi: e.target.value })} /></div>
                </div>
              </div>
            </div>
          )}
          {detail && !editMode && detail.approval_status === 'beklemede' && (
            <DialogFooter>
              <Button variant="destructive" disabled={actionSaving} onClick={() => { setDetail(null); openReject([detail.id]); }}><X className="w-4 h-4 mr-1" />Reddet</Button>
              <Button className="bg-green-600 hover:bg-green-700" disabled={actionSaving} onClick={() => { setDetail(null); handleApprove([detail.id]); }}><Check className="w-4 h-4 mr-1" />Onayla</Button>
            </DialogFooter>
          )}
          {detail && editMode && editForm && (
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditMode(false); setEditForm(null); }} disabled={editSaving}>İptal</Button>
              <Button disabled={editSaving} onClick={async () => {
                setEditSaving(true);
                try {
                  const payload = {
                    name: editForm.name, surname: editForm.surname,
                    email: editForm.email, phone: editForm.phone, tc_kimlik: editForm.tc_kimlik,
                    cinsiyet: editForm.cinsiyet, egitim_durumu: editForm.egitim_durumu,
                    meslek: editForm.meslek, dogum_tarihi: editForm.dogum_tarihi,
                    okul_adi: editForm.okul_adi, uyesi_oldugu_kurulus: editForm.uyesi_oldugu_kurulus,
                    acik_adres: editForm.acik_adres,
                    muhtar_bilgileri: {
                      muhtarlik_adresi: editForm.muhtarlik_adresi,
                      muhtarlik_telefon: editForm.muhtarlik_telefon,
                      aza_sayisi: editForm.aza_sayisi,
                    },
                  };
                  const r = await muhtarApplicationsAPI.update(detail.id, payload);
                  toast.success('Muhtar bilgileri güncellendi');
                  setDetail(r.data);
                  setEditMode(false);
                  setEditForm(null);
                  load && load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Güncelleme başarısız');
                } finally {
                  setEditSaving(false);
                }
              }} data-testid="muhtar-save-btn">
                {editSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Kaydet
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Reason Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Basvuruyu Reddet</DialogTitle>
            <DialogDescription>{rejectIds.length} basvuru reddedilecek. Red sebebi opsiyonel olarak kullaniciya iletilir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Red Sebebi (opsiyonel)</Label>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Ornek: Muhtarlik belgesi dogrulanamadi" rows={4} data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={actionSaving}>Iptal</Button>
            <Button variant="destructive" disabled={actionSaving} onClick={submitReject} data-testid="reject-submit">
              {actionSaving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Reddediliyor...</> : <><X className="w-4 h-4 mr-1" />Reddet</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
